import { useCallback, useMemo, useRef } from 'react'
import { Radar } from 'react-chartjs-2'
import type { Chart as ChartJS, ChartData, ChartOptions, LegendItem } from 'chart.js'

import { ensureChartJsRegistered, resolveCanvasColor, withCanvasAlpha, chartJsPdfReadyPlugin, pickDeckChartPalette } from '@/lib/charts/chartjs'
import { chartTooltipPreset } from '../chart-tooltip-preset'
import { CHART_DEFAULTS } from '../../../foundations/constants'
import { CHART_LIMITS } from '../../../foundations/slide-limits'

export interface RadarSeries {
  label: string
  data: number[]
  color?: string
  fill?: boolean
  dashed?: boolean
}

export interface RadarChartProps {
  labels: string[]
  series: RadarSeries[]
  min?: number
  max?: number
  height?: number | string
}

function withAlpha(color: string, alpha01: number): string {
  const a = Math.max(0, Math.min(1, alpha01))
  const c = (color || '').trim()
  if (!c) return c
  if (c.startsWith('rgb(')) return c.replace(/^rgb\(/, 'rgba(').replace(/\)\s*$/, `, ${a})`)
  if (c.startsWith('rgba(')) return c.replace(/,\s*([0-9]*\.?[0-9]+)\s*\)\s*$/, `, ${a})`)
  if (c.startsWith('#')) {
    const hex = c.slice(1)
    const full = hex.length === 3 ? hex.split('').map((x) => x + x).join('') : hex
    if (full.length === 6) {
      const n = Number.parseInt(full, 16)
      const r = (n >> 16) & 255
      const g = (n >> 8) & 255
      const b = n & 255
      return `rgba(${r}, ${g}, ${b}, ${a})`
    }
  }
  // Unknown format; best-effort: return as-is (no fill alpha change).
  return c
}

/**
 * @deprecated 2026-10-01: legacy reader only. Do not use in new code; draw the shared
 * plots in `packages/deck-ui/src/plots` instead. Kept so old studies the figure service
 * refuses (and cards whose figure fails) still draw exactly as before.
 * Delete after 2027-01-01, together with the legacy fallback in QuestionCard.
 */
export function RadarChart({ labels, series, min = 0, max = 5, height = '100%' }: RadarChartProps) {
  ensureChartJsRegistered()

  const chartRef = useRef<ChartJS<'radar'>>(null)
  const soloIndexRef = useRef<number | null>(null)

  const defaultSeriesColors = useMemo(() => pickDeckChartPalette(Math.max(1, series.length)), [series.length])

  // Enforce safety limit on labels and corresponding data
  const limitedLabels = useMemo(() => labels.slice(0, CHART_LIMITS.radarMaxItems), [labels])

  const chartData = useMemo(() => {
    return {
      labels: limitedLabels,
      datasets: series.map((s, idx) => {
        const color = resolveCanvasColor(s.color || defaultSeriesColors[idx] || 'var(--slide-chart-1)')
        return {
          label: s.label,
          data: s.data.slice(0, CHART_LIMITS.radarMaxItems),
          borderColor: color,
          backgroundColor: s.fill ? withAlpha(color, 0.15) : 'transparent',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointBackgroundColor: color,
          pointBorderColor: color,
          fill: Boolean(s.fill),
          borderDash: s.dashed ? [5, 5] : undefined,
        }
      }),
    } satisfies ChartData<'radar'>
  }, [limitedLabels, series, defaultSeriesColors])

  const handleLegendClick = useCallback((_e: any, legendItem: LegendItem) => {
    const chart = chartRef.current
    if (!chart) return
    const clickedIdx = legendItem.datasetIndex
    if (clickedIdx == null) return

    if (soloIndexRef.current === clickedIdx) {
      soloIndexRef.current = null
      chart.data.datasets.forEach((_, i) => { chart.setDatasetVisibility(i, true) })
    } else {
      soloIndexRef.current = clickedIdx
      chart.data.datasets.forEach((_, i) => { chart.setDatasetVisibility(i, i === clickedIdx) })
    }
    chart.update()
  }, [])

  const textColor = resolveCanvasColor('var(--foreground)', 'var(--slide-text-primary)')
  const mutedColor = resolveCanvasColor('var(--muted-foreground)', 'var(--slide-text-muted)')
  const gridColor = withAlpha(mutedColor, 0.25)
  const angleColor = withAlpha(mutedColor, 0.18)

  const options: ChartOptions<'radar'> = useMemo(
    () => ({
      plugins: {
        legend: {
          position: 'bottom',
          onClick: handleLegendClick,
          onHover: (event) => {
            const target = event.native?.target as HTMLElement | undefined
            if (target) target.style.cursor = 'pointer'
          },
          onLeave: (event) => {
            const target = event.native?.target as HTMLElement | undefined
            if (target) target.style.cursor = 'default'
          },
          labels: {
            font: { size: CHART_DEFAULTS.tickFontSize.md, weight: 500 as const },
            color: textColor,
            usePointStyle: true,
            pointStyle: 'line',
            padding: 20,
            generateLabels: (chart) => {
              const solo = soloIndexRef.current
              return chart.data.datasets.map((ds, i) => {
                const active = solo === null || solo === i
                return {
                  text: ds.label || '',
                  datasetIndex: i,
                  fillStyle: active ? (ds.borderColor as string) : withAlpha(ds.borderColor as string, 0.15),
                  strokeStyle: active ? (ds.borderColor as string) : withAlpha(ds.borderColor as string, 0.15),
                  fontColor: active ? textColor : withAlpha(textColor, 0.25),
                  lineWidth: 2,
                  hidden: false,
                  pointStyle: 'line' as const,
                }
              })
            },
          },
        },
        // Radar is multi-series: the title row (the axis point label) is
        // meaningful and distinct from each per-series body row, so keep it
        // and only apply the shared light-surface styling.
        tooltip: chartTooltipPreset({ dropTitle: false }),
      },
      scales: {
        r: {
          min,
          max,
          ticks: {
            display: false,
            maxTicksLimit: 4,
            backdropColor: 'transparent',
          },
          grid: {
            color: gridColor,
            lineWidth: 1,
          },
          angleLines: {
            color: angleColor,
            lineWidth: 1,
          },
          pointLabels: {
            font: { size: CHART_DEFAULTS.tickFontSize.md, weight: 500 as const },
            color: textColor,
          },
        },
      },
    }),
    [min, max, textColor, mutedColor, gridColor, angleColor, handleLegendClick]
  )

  const resolvedHeight = typeof height === 'number' ? `${height}px` : height

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        height: resolvedHeight,
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
      }}
      data-chart-library="chartjs"
    >
      <Radar ref={chartRef} data={chartData} options={options} plugins={[chartJsPdfReadyPlugin]} />
    </div>
  )
}
